import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PAGINATION } from "@/lib/constants";
import { buildSearchParams } from "@/lib/queryParams";

const debounceTime = 500;

type UseSearchPaginationOptions = {
  syncSearchWithQuery?: boolean;
  syncPageWithQuery?: boolean;
  syncRowsPerPageWithQuery?: boolean;
};

const getPositiveNumber = (value: string | null, fallback: number) => {
  const parsedValue = Number.parseInt(value ?? "", 10);
  return Number.isNaN(parsedValue) || parsedValue < 1 ? fallback : parsedValue;
};

export function useSearchPagination(options: UseSearchPaginationOptions = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { syncSearchWithQuery = true, syncPageWithQuery = true, syncRowsPerPageWithQuery = true } = options;

  const searchParam = syncSearchWithQuery ? searchParams.get("search") : null;
  const pageParam = syncPageWithQuery
    ? getPositiveNumber(searchParams.get("pageNo"), PAGINATION.DEFAULT_PAGE_NUMBER)
    : PAGINATION.DEFAULT_PAGE_NUMBER;
  const rowsPerPageParam = syncRowsPerPageWithQuery
    ? getPositiveNumber(searchParams.get("perPage"), PAGINATION.DEFAULT_ROWS_PER_PAGE)
    : PAGINATION.DEFAULT_ROWS_PER_PAGE;

  const [search, setSearch] = useState<string | null>(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState<string | null>(searchParam);
  const [pageSelected, setPageSelected] = useState(pageParam);
  const [rowsPerPageValue, setRowsPerPageValue] = useState(rowsPerPageParam);

  useEffect(() => {
    if (!syncSearchWithQuery) return;
    setSearch(searchParam);
    setDebouncedSearch(searchParam);
  }, [searchParam, syncSearchWithQuery]);

  useEffect(() => {
    if (!syncPageWithQuery) return;
    setPageSelected(pageParam);
  }, [pageParam, syncPageWithQuery]);

  useEffect(() => {
    if (!syncRowsPerPageWithQuery) return;
    setRowsPerPageValue(rowsPerPageParam);
  }, [rowsPerPageParam, syncRowsPerPageWithQuery]);

  useEffect(() => {
    if ((search ?? "") === (debouncedSearch ?? "")) return;

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
    }, debounceTime);

    return () => window.clearTimeout(timeoutId);
  }, [search, debouncedSearch]);

  useEffect(() => {
    const searchQueryValue = syncSearchWithQuery ? debouncedSearch || undefined : undefined;
    const pageQueryValue = syncPageWithQuery
      ? pageSelected === PAGINATION.DEFAULT_PAGE_NUMBER
        ? undefined
        : pageSelected
      : undefined;
    const rowsPerPageQueryValue = syncRowsPerPageWithQuery
      ? rowsPerPageValue === PAGINATION.DEFAULT_ROWS_PER_PAGE
        ? undefined
        : rowsPerPageValue
      : undefined;

    const values = {
      ...(syncSearchWithQuery ? { search: searchQueryValue } : {}),
      ...(syncPageWithQuery ? { pageNo: pageQueryValue } : {}),
      ...(syncRowsPerPageWithQuery ? { perPage: rowsPerPageQueryValue } : {}),
    };

    const keysToReset = [
      ...(syncSearchWithQuery ? [] : ["search"]),
      ...(syncPageWithQuery ? [] : ["pageNo"]),
      ...(syncRowsPerPageWithQuery ? [] : ["perPage"]),
    ];

    setSearchParams((previousParams) => {
      const nextParams = buildSearchParams(previousParams, values, keysToReset);
      return nextParams.toString() === previousParams.toString() ? previousParams : nextParams;
    }, { replace: true });
  }, [
    setSearchParams,
    syncPageWithQuery,
    syncRowsPerPageWithQuery,
    syncSearchWithQuery,
    debouncedSearch,
    pageSelected === PAGINATION.DEFAULT_PAGE_NUMBER ? undefined : syncPageWithQuery ? pageSelected : undefined,
    rowsPerPageValue === PAGINATION.DEFAULT_ROWS_PER_PAGE ? undefined : syncRowsPerPageWithQuery ? rowsPerPageValue : undefined,
  ]);

  const reset = useCallback(() => {
    setSearch(null);
    setDebouncedSearch(null);
    setPageSelected(PAGINATION.DEFAULT_PAGE_NUMBER);
    setRowsPerPageValue(PAGINATION.DEFAULT_ROWS_PER_PAGE);
  }, []);

  return {
    debouncedSearch,
    search,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
    reset,
  };
}
